import { CalendarEvent } from '../../hooks/useSupabaseEvents';

// Resolve an event's calendar — primary: calendar_id, fallback: color.name (for pre-migration data)
export const resolveCalendar = (
  event: CalendarEvent,
  calendars: Array<{ id: string; name: string; color: string }>
): { id: string; name: string; color: string } | null => {
  if (event.calendar_id) {
    const match = calendars.find(c => c.id === event.calendar_id);
    if (match) return match;
  }
  const eventColor = event.color as any;
  const calendarName = eventColor?.name;
  if (calendarName) {
    const match = calendars.find(c => c.name === calendarName);
    if (match) return match;
  }
  // Imported events carry their calendar color — return a synthetic calendar entry
  if (event.isImported && eventColor?.bg) {
    return { id: `imported-${eventColor.name}`, name: eventColor.name, color: eventColor.bg };
  }
  return null;
};

export const sortEventsForDisplay = (events: CalendarEvent[]): CalendarEvent[] => {
  return events.sort((a, b) => {
    // All-day events come first
    if (a.all_day && !b.all_day) return -1;
    if (!a.all_day && b.all_day) return 1;

    // For timed events, sort by start time
    if (!a.all_day && !b.all_day && a.start_time && b.start_time) {
      return a.start_time.localeCompare(b.start_time);
    }

    // Otherwise sort by title
    return a.title.localeCompare(b.title);
  });
};

export const getEventsForDate = (events: CalendarEvent[], dateKey: string): CalendarEvent[] => {
  return events.filter(event => event.dates.includes(dateKey));
};

export const getEventDuration = (event: CalendarEvent): number => {
  return event.dates.length;
};

export const isEventStartingBeforeMonth = (
  event: CalendarEvent,
  year: number,
  month: number
): boolean => {
  if (!event.dates || event.dates.length === 0) return false;

  const firstDate = event.dates[0];
  const [eventYear, eventMonth] = firstDate.split('-').map(Number);

  // Event starts before if year is less, or same year but month is less
  if (eventYear < year) return true;
  if (eventYear === year && eventMonth < month + 1) return true;

  return false;
};

export const isEventContinuingAfterMonth = (
  event: CalendarEvent,
  year: number,
  month: number
): boolean => {
  if (!event.dates || event.dates.length === 0) return false;

  const lastDate = event.dates[event.dates.length - 1];
  const [eventYear, eventMonth] = lastDate.split('-').map(Number);

  // Event continues after if year is greater, or same year but month is greater
  if (eventYear > year) return true;
  if (eventYear === year && eventMonth > month + 1) return true;

  return false;
};

export const getEventDatesInMonth = (
  event: CalendarEvent,
  year: number,
  month: number
): string[] => {
  // Use string prefix matching to avoid timezone issues with new Date() parsing
  const prefix = `${year}-${String(month + 1).padStart(2, '0')}-`;
  return event.dates.filter(dateKey => dateKey.startsWith(prefix));
};

export const filterEventsByTags = (
  events: CalendarEvent[],
  selectedTagFilters: Set<string>,
  calendars: Array<{ id: string; name: string; color: string }>
): CalendarEvent[] => {
  // Check if unassigned filter is selected
  const includeUnassigned = selectedTagFilters.has(UNASSIGNED_CALENDAR_ID);

  // Get valid filter IDs (excluding unassigned special ID)
  const validFilterIds = Array.from(selectedTagFilters).filter(id =>
    id !== UNASSIGNED_CALENDAR_ID && calendars.some(c => c.id === id)
  );

  // Count unassigned events to know if we should treat them specially
  const hasUnassignedEvents = events.some(e => isEventUnassigned(e, calendars));

  // Total selectable items = calendars + (unassigned if exists)
  const totalSelectableCount = calendars.length + (hasUnassignedEvents ? 1 : 0);

  // Count of selected items
  const selectedCount = validFilterIds.length + (includeUnassigned ? 1 : 0);

  // If no calendars exist and no unassigned events, show all events
  if (calendars.length === 0 && !hasUnassignedEvents) return events;

  // If filters are stale (none match current calendars and unassigned not selected), show all events
  // This happens during space transition before filters are re-initialized
  if (selectedTagFilters.size > 0 && selectedCount === 0) return events;

  // If all items are selected, show all events (optimization)
  if (selectedCount === totalSelectableCount) return events;

  // If nothing selected, show nothing
  if (selectedCount === 0) return [];

  return events.filter(event => {
    // Check if event is unassigned
    if (isEventUnassigned(event, calendars)) {
      return includeUnassigned;
    }

    // Check if event matches any selected calendar
    const cal = resolveCalendar(event, calendars);
    return cal ? validFilterIds.includes(cal.id) : false;
  });
};

export const filterEventsBySearch = (
  events: CalendarEvent[],
  searchQuery: string
): CalendarEvent[] => {
  if (!searchQuery.trim()) return events;

  const query = searchQuery.toLowerCase();

  return events.filter(event => {
    const matchesTitle = event.title.toLowerCase().includes(query);
    const matchesNotes = event.notes?.toLowerCase().includes(query) || false;
    const matchesDate = event.dates.some(date => date.includes(searchQuery));
    return matchesTitle || matchesNotes || matchesDate;
  });
};

// Special ID for unassigned events filter
export const UNASSIGNED_CALENDAR_ID = '__unassigned__';

// Check if an event has no valid calendar assignment
export const isEventUnassigned = (
  event: CalendarEvent,
  calendars: Array<{ id: string; name: string; color: string }>
): boolean => {
  return resolveCalendar(event, calendars) === null;
};

// Get unassigned events
export const getUnassignedEvents = (
  events: CalendarEvent[],
  calendars: Array<{ id: string; name: string; color: string }>
): CalendarEvent[] => {
  return events.filter(event => isEventUnassigned(event, calendars));
};
